import * as xlsx from 'xlsx-js-style'
import colors from '../../styles/color'

/**
 * Excel style configuration constants
 */
export const EXCEL_STYLE_CONSTANTS = {
  FONT: {
    NAME: 'Calibri',
    SIZE: 11,
    COLOR: colors.black,
  },
  COLORS: {
    LIGHT_RED: colors.lightRed,
    LIGHT_GREEN: colors.lightGreen,
    LIGHT_BLUE: colors.lightBlue,
    BLACK: colors.black,
  },
}

/**
 * Common alignment definitions for Excel cells
 */
export const EXCEL_ALIGNMENTS: Record<string, xlsx.CellStyle['alignment']> = {
  HEADER_CENTER_BOTTOM: {
    horizontal: 'center',
    vertical: 'bottom',
    wrapText: true,
  },
  HEADER_LEFT_CENTER: {
    horizontal: 'left',
    vertical: 'center',
    wrapText: true,
  },
  CELL_CENTER: {
    horizontal: 'center',
    vertical: 'center',
  },
  CELL_RIGHT: {
    horizontal: 'right',
    vertical: 'center',
  },
  CELL_LEFT: {
    horizontal: 'left',
    vertical: 'center',
  },
  CELL_VERTICAL_CENTER: {
    vertical: 'center',
  },
  DEFAULT_CENTER: {
    vertical: 'center',
  },
}

/**
 * Base style definition for all Excel cells
 */
export const EXCEL_BASE_STYLE: xlsx.CellStyle = {
  border: {
    top: {
      style: 'thin',
      color: { rgb: colors.black },
    },
    bottom: {
      style: 'thin',
      color: { rgb: colors.black },
    },
    left: {
      style: 'thin',
      color: { rgb: colors.black },
    },
    right: {
      style: 'thin',
      color: { rgb: colors.black },
    },
  },
  font: {
    name: EXCEL_STYLE_CONSTANTS.FONT.NAME,
    sz: EXCEL_STYLE_CONSTANTS.FONT.SIZE,
    color: { rgb: EXCEL_STYLE_CONSTANTS.FONT.COLOR },
  },
}

/**
 * Creates a header style with specified fill color and alignment
 */
export const createExcelHeaderStyle = (
  fillColor: string,
  alignment: xlsx.CellStyle['alignment'] = EXCEL_ALIGNMENTS.HEADER_CENTER_BOTTOM
): xlsx.CellStyle => ({
  ...EXCEL_BASE_STYLE,
  fill: {
    patternType: 'solid',
    // xlsx-js-style requires RGB format without '#' prefix
    fgColor: { rgb: fillColor.slice(1) },
  },
  alignment,
})

/**
 * Creates a data cell style with specified alignment and optional number formatting
 */
export const createExcelDataCellStyle = (
  alignment: (typeof EXCEL_ALIGNMENTS)[keyof typeof EXCEL_ALIGNMENTS],
  hasNumberFormat?: boolean
) => ({
  ...EXCEL_BASE_STYLE,
  alignment,
  ...(hasNumberFormat && { numFmt: '#,##0' }),
})

/**
 * Pre-defined Excel cell styles for verification sheet
 */
export const EXCEL_CELL_STYLES: Record<string, xlsx.CellStyle> = {
  // Header styles
  TENANT_DETAIL_HEADER: createExcelHeaderStyle(
    EXCEL_STYLE_CONSTANTS.COLORS.LIGHT_RED
  ),
  LIGHT_GREEN_HEADER: createExcelHeaderStyle(
    EXCEL_STYLE_CONSTANTS.COLORS.LIGHT_GREEN
  ),
  LIGHT_BLUE_HEADER: createExcelHeaderStyle(
    EXCEL_STYLE_CONSTANTS.COLORS.LIGHT_BLUE
  ),
  HEADER: createExcelHeaderStyle(
    EXCEL_STYLE_CONSTANTS.COLORS.LIGHT_RED,
    EXCEL_ALIGNMENTS.HEADER_LEFT_CENTER
  ),
  STATS_HEADER: createExcelHeaderStyle(
    EXCEL_STYLE_CONSTANTS.COLORS.LIGHT_RED,
    EXCEL_ALIGNMENTS.HEADER_LEFT_CENTER
  ),

  // Data cell styles
  DATA_CELL: createExcelDataCellStyle(EXCEL_ALIGNMENTS.CELL_VERTICAL_CENTER),
  NUMERIC_CELL: createExcelDataCellStyle(EXCEL_ALIGNMENTS.CELL_RIGHT, true),
  CENTER_CELL: createExcelDataCellStyle(EXCEL_ALIGNMENTS.CELL_CENTER),
  RIGHT_ALIGNED_TEXT: createExcelDataCellStyle(EXCEL_ALIGNMENTS.CELL_RIGHT),
  LEFT_ALIGNED_TEXT: createExcelDataCellStyle(EXCEL_ALIGNMENTS.CELL_LEFT),
}
